import React, { useEffect, useState } from 'react';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import SearchAndFilter from '../../components/ui/SearchAndFilter';
import { usePaginatedData } from '../../hooks/usePaginatedData';

interface Organization {
  id: string;
  name: string;
  email?: string;
  callback_url?: string;
  max_interview_duration: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
}

interface OrganizationForm {
  id: string;
  name: string;
  email: string;
  callback_url: string;
  max_interview_duration: number;
  max_attempts: number;
}

export const Organizations: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [form, setForm] = useState<OrganizationForm>({ id: '', name: '', email: '', callback_url: '', max_interview_duration: 60, max_attempts: 1 });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Pagination and filtering
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData: paginatedOrganizations,
    setCurrentPage,
    setItemsPerPage,
    searchQuery,
    setSearchQuery,
    filters,
    setFilter,
    clearFilters,
    totalFilteredItems,
  } = usePaginatedData({
    data: organizations,
    searchFields: ['id', 'name', 'email', 'callback_url'],
    itemsPerPageDefault: 10,
    customFilters: {
      has_webhook: (org: Organization, value: string) => {
        if (value === 'yes') {
          return !!(org.callback_url && org.callback_url.trim() !== '');
        } else if (value === 'no') {
          return !(org.callback_url && org.callback_url.trim() !== '');
        }
        return true;
      },
    },
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      } else {
        setError('Failed to fetch organizations');
      }
    } catch (error) {
      setError('Network error while fetching organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const url = editingOrg 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/admin/organizations/${editingOrg.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/admin/organizations`;
      
      const method = editingOrg ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setSuccess(editingOrg ? 'Organization updated successfully' : 'Organization created successfully');
        setIsModalOpen(false);
        setEditingOrg(null);
        setForm({ id: '', name: '', email: '', callback_url: '', max_interview_duration: 60, max_attempts: 1 });
        fetchOrganizations();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setForm({ id: org.id, name: org.name, email: org.email || '', callback_url: org.callback_url || '', max_interview_duration: org.max_interview_duration ?? 60, max_attempts: org.max_attempts ?? 1 });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this organization?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/organizations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess('Organization deleted successfully');
        fetchOrganizations();
      } else {
        setError('Failed to delete organization');
      }
    } catch (error) {
      setError('Network error while deleting organization');
    }
  };

  const openCreateModal = () => {
    setEditingOrg(null);
    setForm({ id: '', name: '', email: '', callback_url: '', max_interview_duration: 60, max_attempts: 1 });
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-600 mt-2">Manage client organizations and their settings</p>
        </div>
        <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700">
          Add Organization
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Search and Filters */}
      <SearchAndFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search organizations by ID, name, email, or callback URL..."
        filters={[
          {
            label: "Has Webhook URL",
            key: "has_webhook",
            value: filters.has_webhook || '',
            options: [
              { label: "Has Webhook", value: "yes" },
              { label: "No Webhook", value: "no" },
            ],
            onChange: (value) => setFilter('has_webhook', value),
          },
        ]}
        onClearFilters={clearFilters}
      />

      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Organizations</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Callback URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration (min)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Attempts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{org.id}</code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{org.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{org.email || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600 max-w-xs truncate" title={org.callback_url}>
                      {org.callback_url || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{org.max_interview_duration ?? 60}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{org.max_attempts ?? 1}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {new Date(org.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(org)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(org.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedOrganizations.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">🏢</div>
              <p className="text-gray-500">
                {totalFilteredItems === 0
                  ? searchQuery || Object.values(filters).some(f => f)
                    ? 'No organizations match your search criteria'
                    : 'No organizations found'
                  : 'No organizations on this page'}
              </p>
              {totalFilteredItems === 0 && !searchQuery && !Object.values(filters).some(f => f) && (
                <Button onClick={openCreateModal} className="mt-4 bg-blue-600 hover:bg-blue-700">
                  Create your first organization
                </Button>
              )}
              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear search and filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalFilteredItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingOrg ? 'Edit Organization' : 'Create Organization'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-2">
              Organization ID
            </label>
            <input
              type="text"
              id="id"
              value={form.id}
              onChange={(e) => setForm(prev => ({ ...prev, id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={!!editingOrg}
            />
            <p className="text-xs text-gray-500 mt-1">
              Used as the tenant identifier in candidate IDs (e.g., "company1" for candidate "company1-123")
            </p>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name
            </label>
            <input
              type="text"
              id="name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email (Optional)
            </label>
            <input
              type="email"
              id="email"
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="callback_url" className="block text-sm font-medium text-gray-700 mb-2">
              Webhook URL (Optional)
            </label>
            <input
              type="url"
              id="callback_url"
              value={form.callback_url}
              onChange={(e) => setForm(prev => ({ ...prev, callback_url: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://your-domain.com/webhook/interview-reports"
            />
            <p className="text-xs text-gray-500 mt-1">
              URL where completed interview reports will be sent via POST request
            </p>
          </div>

          <div>
            <label htmlFor="max_interview_duration" className="block text-sm font-medium text-gray-700 mb-2">
              Max Interview Duration (minutes)
            </label>
            <input
              type="number"
              id="max_interview_duration"
              min={5}
              max={180}
              value={form.max_interview_duration}
              onChange={(e) => setForm(prev => ({ ...prev, max_interview_duration: parseInt(e.target.value) || 60 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum duration allowed for each interview session
            </p>
          </div>

          <div>
            <label htmlFor="max_attempts" className="block text-sm font-medium text-gray-700 mb-2">
              Max Interview Attempts
            </label>
            <input
              type="number"
              id="max_attempts"
              min={1}
              max={50}
              value={form.max_attempts}
              onChange={(e) => setForm(prev => ({ ...prev, max_attempts: parseInt(e.target.value) || 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of interview attempts allowed per candidate per position. Completed, cancelled, and failed interviews all count.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              {editingOrg ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};